import { useEffect, useMemo, useState } from 'react';
import {
  Boxes,
  Check,
  Database,
  Layers,
  Plus,
  RotateCcw,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Tags,
  X,
} from 'lucide-react';

import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { KpiCard, formatNumber } from '@/features/dashboard/widgets';
import { useReportDatasets } from '@/features/reporting/hooks';
import { classifyColumn } from '@/features/domains/classify';
import {
  loadCustomDomains,
  loadDecisions,
  saveCustomDomains,
  saveDecisions,
  type StoredDecision,
} from '@/features/domains/storage';
import {
  accentClasses,
  DOMAIN_GROUPS,
  PACKAGED_DOMAINS,
  type DataDomain,
  type DomainGroup,
} from '@/features/domains/types';
import type { ApiError } from '@/lib/errors';

type Category = 'accepted' | 'suggested' | 'rejected' | 'unclassified';

interface Row {
  key: string;
  datasetId: string;
  datasetName: string;
  column: string;
  dataType: string;
  domainId: string | null;
  confidence: number;
  category: Category;
}

const CATEGORY_META: Record<Category, { label: string; cls: string }> = {
  accepted: { label: 'Classified', cls: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' },
  suggested: { label: 'Suggested', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
  unclassified: { label: 'Unclassified', cls: 'bg-ink-100 text-ink-500 dark:bg-white/10 dark:text-ink-300' },
};

const inputClass =
  'h-9 rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:border-white/10 dark:bg-ink-900 dark:text-ink-50';

function DomainBadge({
  domain,
  group,
}: {
  domain: DataDomain | undefined;
  group: DomainGroup | undefined;
}): JSX.Element {
  if (!domain || !group) {
    return <span className="text-xs text-ink-400 dark:text-ink-500">—</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${accentClasses(
        group.accent,
      )}`}
    >
      {domain.name}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }): JSX.Element {
  const color = value >= 90 ? '#10b981' : value >= 75 ? '#f59e0b' : '#94a3b8';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-100 dark:bg-white/10">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="w-8 font-mono text-[11px] tabular-nums text-ink-500 dark:text-ink-400">{value}%</span>
    </div>
  );
}

export function DataDomainsPage(): JSX.Element {
  const datasetsQ = useReportDatasets();
  const datasets = useMemo(() => datasetsQ.data ?? [], [datasetsQ.data]);

  const [decisions, setDecisions] = useState<Record<string, StoredDecision>>(() => loadDecisions());
  const [customDomains, setCustomDomains] = useState<DataDomain[]>(() => loadCustomDomains());
  const [datasetFilter, setDatasetFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | Category>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => saveDecisions(decisions), [decisions]);
  useEffect(() => saveCustomDomains(customDomains), [customDomains]);

  const allDomains = useMemo(() => [...PACKAGED_DOMAINS, ...customDomains], [customDomains]);
  const domainMap = useMemo(() => new Map(allDomains.map((d) => [d.id, d])), [allDomains]);
  const groupMap = useMemo(() => new Map(DOMAIN_GROUPS.map((g) => [g.id, g])), []);

  // Live classification of every real column, overlaid with saved decisions.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const ds of datasets) {
      for (const col of ds.columns) {
        const key = `${ds.id}::${col.name}`;
        const suggestion = classifyColumn(col.name, col.data_type, ds.name, allDomains);
        const decision = decisions[key];
        let domainId: string | null;
        let confidence: number;
        let category: Category;

        if (decision) {
          domainId = decision.domainId;
          if (decision.status === 'rejected') {
            category = 'rejected';
            confidence = 0;
          } else {
            category = 'accepted';
            confidence =
              suggestion && suggestion.domainId === domainId
                ? suggestion.confidence
                : decision.source === 'manual'
                  ? 100
                  : (suggestion?.confidence ?? 0);
          }
        } else if (suggestion) {
          domainId = suggestion.domainId;
          confidence = suggestion.confidence;
          category = 'suggested';
        } else {
          domainId = null;
          confidence = 0;
          category = 'unclassified';
        }

        out.push({
          key,
          datasetId: ds.id,
          datasetName: ds.name,
          column: col.name,
          dataType: col.data_type,
          domainId,
          confidence,
          category,
        });
      }
    }
    return out;
  }, [datasets, allDomains, decisions]);

  const stats = useMemo(() => {
    const total = rows.length;
    const accepted = rows.filter((r) => r.category === 'accepted');
    const pending = rows.filter((r) => r.category === 'suggested').length;
    const domainsInUse = new Set(accepted.map((r) => r.domainId).filter(Boolean)).size;
    return {
      total,
      classified: accepted.length,
      pending,
      domainsInUse,
      coverage: total > 0 ? Math.round((accepted.length / total) * 100) : 0,
    };
  }, [rows]);

  // Accepted-column count per group, for the coverage panel and library counts.
  const perDomainAccepted = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      if (r.category === 'accepted' && r.domainId) m.set(r.domainId, (m.get(r.domainId) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  const perGroupAccepted = useMemo(() => {
    const m = new Map<string, number>();
    for (const [domainId, count] of perDomainAccepted) {
      const g = domainMap.get(domainId)?.groupId;
      if (g) m.set(g, (m.get(g) ?? 0) + count);
    }
    return m;
  }, [perDomainAccepted, domainMap]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (datasetFilter === 'all' || r.datasetId === datasetFilter) &&
        (statusFilter === 'all' || r.category === statusFilter) &&
        (q === '' || r.column.toLowerCase().includes(q)),
    );
  }, [rows, datasetFilter, statusFilter, search]);

  // --- decision mutations ---------------------------------------------------
  function setDecision(key: string, decision: StoredDecision | null): void {
    setDecisions((prev) => {
      const next = { ...prev };
      if (decision === null) delete next[key];
      else next[key] = decision;
      return next;
    });
  }

  function acceptAllHighConfidence(): void {
    setDecisions((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (r.category === 'suggested' && r.domainId && r.confidence >= 90) {
          next[r.key] = { domainId: r.domainId, status: 'accepted', source: 'auto' };
        }
      }
      return next;
    });
  }

  const highConfidencePending = rows.filter(
    (r) => r.category === 'suggested' && r.confidence >= 90,
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow eyebrow-dot mb-2.5">
            <span>MODULE</span>
          </div>
          <h1 className="h-display">Data Domains</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
            Semantic classification for every column in the estate. Domains — IBAN, Account Number, Email, and more —
            are auto-discovered against your live schemas, organised into groups, and governed with an accept / reject
            review workflow.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={acceptAllHighConfidence}
            disabled={highConfidencePending === 0}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-3 text-sm font-medium text-brand-700 transition hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300"
          >
            <Sparkles className="h-4 w-4" />
            Accept all ≥90% ({highConfidencePending})
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            Custom domain
          </button>
        </div>
      </div>

      {datasetsQ.isPending ? (
        <Spinner label="Scanning schemas…" />
      ) : datasetsQ.isError ? (
        <ErrorState error={datasetsQ.error as ApiError} />
      ) : datasets.length === 0 ? (
        <Card>
          <p className="py-6 text-center text-sm text-ink-500 dark:text-ink-400">
            No datasets to classify yet — connect a source or upload a file in <strong>Data Sources</strong> first.
          </p>
        </Card>
      ) : (
        <>
          {/* Coverage KPIs */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard label="Columns Scanned" value={formatNumber(stats.total)} sub={`across ${datasets.length} dataset${datasets.length === 1 ? '' : 's'}`} icon={Database} accent="sky" />
            <KpiCard label="Classified" value={`${stats.coverage}%`} sub={`${formatNumber(stats.classified)} columns accepted`} icon={ShieldCheck} accent="brand" />
            <KpiCard label="Pending Review" value={formatNumber(stats.pending)} sub="AI suggestions awaiting sign-off" icon={ScanSearch} accent="amber" />
            <KpiCard label="Domains In Use" value={formatNumber(stats.domainsInUse)} sub={`of ${allDomains.length} in the library`} icon={Tags} accent="violet" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Domain library */}
            <Card title="Domain library" description="Packaged banking domains, grouped. Counts show classified columns.">
              <div className="space-y-4">
                {DOMAIN_GROUPS.map((group) => {
                  const domains = allDomains.filter((d) => d.groupId === group.id);
                  return (
                    <div key={group.id}>
                      <div className="mb-1.5 flex items-center gap-2">
                        <Boxes className="h-3.5 w-3.5 text-ink-400 dark:text-ink-500" />
                        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500 dark:text-ink-400">
                          {group.name}
                        </span>
                        <span className="text-[10px] text-ink-400 dark:text-ink-500">
                          {perGroupAccepted.get(group.id) ?? 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {domains.map((d) => {
                          const n = perDomainAccepted.get(d.id) ?? 0;
                          return (
                            <span
                              key={d.id}
                              title={d.description}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset ${accentClasses(
                                group.accent,
                              )} ${n === 0 ? 'opacity-45' : ''}`}
                            >
                              {d.name}
                              {n > 0 && <span className="font-mono font-semibold">·{n}</span>}
                              {d.custom && <span className="opacity-70">✎</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Assignments table */}
            <div className="lg:col-span-2">
              <Card title="Column classifications" description="Auto-discovered domain per column. Accept, reject, or reassign.">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <select className={inputClass} value={datasetFilter} onChange={(e) => setDatasetFilter(e.target.value)}>
                    <option value="all">All datasets</option>
                    {datasets.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <select className={inputClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | Category)}>
                    <option value="all">All statuses</option>
                    <option value="accepted">Classified</option>
                    <option value="suggested">Suggested</option>
                    <option value="rejected">Rejected</option>
                    <option value="unclassified">Unclassified</option>
                  </select>
                  <input
                    className={`${inputClass} flex-1 min-w-[140px]`}
                    placeholder="Search column…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b border-ink-100 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-400 dark:border-white/10 dark:text-ink-500">
                        <th className="py-2 pr-3 font-medium">Column</th>
                        <th className="py-2 pr-3 font-medium">Dataset</th>
                        <th className="py-2 pr-3 font-medium">Detected domain</th>
                        <th className="py-2 pr-3 font-medium">Confidence</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100 dark:divide-white/5">
                      {filtered.map((r) => {
                        const domain = r.domainId ? domainMap.get(r.domainId) : undefined;
                        const group = domain ? groupMap.get(domain.groupId) : undefined;
                        const meta = CATEGORY_META[r.category];
                        return (
                          <tr key={r.key} className="align-middle">
                            <td className="py-2.5 pr-3">
                              <div className="font-mono text-xs font-medium text-ink-800 dark:text-ink-100">{r.column}</div>
                              <div className="text-[10px] text-ink-400 dark:text-ink-500">{r.dataType}</div>
                            </td>
                            <td className="py-2.5 pr-3 text-xs text-ink-500 dark:text-ink-400">{r.datasetName}</td>
                            <td className="py-2.5 pr-3">
                              <DomainBadge domain={domain} group={group} />
                            </td>
                            <td className="py-2.5 pr-3">
                              {r.category === 'accepted' || r.category === 'suggested' ? (
                                <ConfidenceBar value={r.confidence} />
                              ) : (
                                <span className="text-xs text-ink-400 dark:text-ink-500">—</span>
                              )}
                            </td>
                            <td className="py-2.5 pr-3">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                                {meta.label}
                              </span>
                            </td>
                            <td className="py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                {r.category === 'suggested' && (
                                  <>
                                    <button
                                      title="Accept"
                                      onClick={() => setDecision(r.key, { domainId: r.domainId, status: 'accepted', source: 'auto' })}
                                      className="rounded-md p-1.5 text-ink-400 transition hover:bg-green-50 hover:text-green-600 dark:hover:bg-white/10"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      title="Reject"
                                      onClick={() => setDecision(r.key, { domainId: null, status: 'rejected', source: 'auto' })}
                                      className="rounded-md p-1.5 text-ink-400 transition hover:bg-red-50 hover:text-accent-crimson dark:hover:bg-white/10"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {(r.category === 'accepted' || r.category === 'rejected') && (
                                  <button
                                    title="Reset to suggestion"
                                    onClick={() => setDecision(r.key, null)}
                                    className="rounded-md p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-white/10 dark:hover:text-ink-200"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </button>
                                )}
                                <select
                                  title="Assign a domain"
                                  className="h-8 max-w-[130px] rounded-md border border-ink-200 bg-white px-1.5 text-xs text-ink-700 dark:border-white/10 dark:bg-ink-900 dark:text-ink-200"
                                  value={r.category === 'accepted' && r.domainId ? r.domainId : ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') setDecision(r.key, null);
                                    else setDecision(r.key, { domainId: v, status: 'accepted', source: 'manual' });
                                  }}
                                >
                                  <option value="">Reassign…</option>
                                  {DOMAIN_GROUPS.map((g) => (
                                    <optgroup key={g.id} label={g.name}>
                                      {allDomains
                                        .filter((d) => d.groupId === g.id)
                                        .map((d) => (
                                          <option key={d.id} value={d.id}>
                                            {d.name}
                                          </option>
                                        ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-sm text-ink-400 dark:text-ink-500">
                            No columns match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {addOpen && (
        <CustomDomainModal
          onClose={() => setAddOpen(false)}
          onSave={(d) => {
            setCustomDomains((prev) => [...prev, d]);
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}

// --- Custom domain modal ------------------------------------------------------

function CustomDomainModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (d: DataDomain) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState<string>(DOMAIN_GROUPS[0]?.id ?? 'identity');
  const [keywords, setKeywords] = useState('');

  const kws = keywords
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
  const canSave = name.trim() !== '' && kws.length > 0;

  function save(): void {
    if (!canSave) return;
    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    onSave({
      id: `custom_${slug}_${Date.now().toString(36)}`,
      name: name.trim(),
      groupId,
      description: 'Custom domain',
      keywords: kws,
      custom: true,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl border border-ink-200 bg-white p-5 shadow-elevated dark:border-white/10 dark:bg-ink-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-2">
          <Layers className="h-4 w-4 text-brand-600" />
          <h3 className="font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
            New custom domain
          </h3>
        </div>
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Domain name</span>
            <input className={`${inputClass} w-full`} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Loan Reference" />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Group</span>
            <select className={`${inputClass} w-full`} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {DOMAIN_GROUPS.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-ink-600 dark:text-ink-300">
              Match keywords <span className="text-ink-400">(comma-separated column names)</span>
            </span>
            <input className={`${inputClass} w-full`} value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="loan_ref, loan_id, facility_no" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-ink-200 px-3 text-sm text-ink-600 transition hover:bg-ink-50 dark:border-white/10 dark:text-ink-300 dark:hover:bg-white/5">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="h-9 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add domain
          </button>
        </div>
      </div>
    </div>
  );
}
